import { useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { changeSituation, getVehicles } from "@/common/services/vehicles.api";
import { IVehicle, STATUS_OPTIONS, VEHICLE_STATUS } from "@/types/vehicle.type";
import { Modal } from "flowbite-react";
import { useEffect, useState } from "react";
import { useGetInventories } from "../../hooks/useGetInventories";
import { STATUS_LABELS } from "@/modules/vehicles/components/TableVehicles";
import { INVENTORY_STATUS_LABELS, IInventory } from "@/types/inventory.types";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useAppStore } from "@/common/states/store";
import { updateInventory } from "@/common/services/inventory-vehicles.api";

interface ModalChangeStatusProps {
  inventoryId: string;
}

export const ModalChangeStatus = ({ inventoryId }: ModalChangeStatusProps) => {
  const [allVehicles, setAllVehicles] = useState<IVehicle[]>([]);
  const [vehicleStatuses, setVehicleStatuses] = useState<
    Record<string, string>
  >({});
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);

  const { notifySuccess, notifyError } = useToastContext();
  const {
    inventories: {
      modals: { isChangeStatusVehiclesOpen },
      closeChangeStatusModal,
    },
  } = useAppStore();

  const { data: inventoriesData, refetch: refetchInventories } =
    useGetInventories();

  const currentInventory: IInventory = inventoriesData?.find(
    (inv) => inv._id === inventoryId,
  ) as IInventory;

  const fetchVehicles = async (pageToFetch: number = 1) => {
    try {
      const response = await getVehicles({
        status: [VEHICLE_STATUS.AVAILABLE],
        itemsPerPage: "20",
        page: pageToFetch,
      });

      if (response.ok) {
        const vehiclesData = response.data;
        const vehiclesNotInInventory = vehiclesData.filter(
          (vehicle) =>
            !currentInventory?.vehicles?.some(
              (invVehicle) => invVehicle.licensePlate === vehicle.licensePlate,
            ),
        );

        if (pageToFetch === 1) {
          setAllVehicles(vehiclesNotInInventory);
        } else {
          setAllVehicles((prev) => [...prev, ...vehiclesNotInInventory]);
        }

        const newStatuses: Record<string, string> = {};
        vehiclesNotInInventory.forEach((vehicle) => {
          newStatuses[vehicle._id] = vehicle.status;
        });
        setVehicleStatuses((prev) => ({ ...prev, ...newStatuses }));

        setHasMore(vehiclesNotInInventory.length === 20);
      }
    } catch (error) {
      console.error("Erro ao buscar veículos:", error);
      notifyError("Erro ao carregar veículos");
    }
  };

  useEffect(() => {
    if (!isChangeStatusVehiclesOpen) return;

    setAllVehicles([]);
    setHasMore(true);

    fetchVehicles();
    refetchInventories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeStatusVehiclesOpen, refetchInventories]);

  const fetchMoreData = () => {
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    fetchVehicles(nextPage);
  };

  const handleVehicleStatusChange = async (
    vehicleId: string,
    newStatus: string,
  ) => {
    try {
      setVehicleStatuses((prev) => ({
        ...prev,
        [vehicleId]: newStatus,
      }));
      const result = await changeSituation(vehicleId, newStatus);
      if (result.ok) {
        notifySuccess("Status do veículo alterado com sucesso!");
        fetchVehicles();
      } else {
        notifyError(result.message || "Erro ao atualizar.");
      }
    } catch (error) {
      console.error("Erro ao se comunicar com o servidor.");
      notifyError("Erro ao atualizar status");
    }
  };

  const getAvailableStatusOptions = (currentStatus: string) => {
    return (
      STATUS_OPTIONS[currentStatus] || [
        { value: "", label: "Sem opções disponíveis" },
      ]
    );
  };

  // Isso aqui parece estranho, mas foi a forma que pensei de trigar
  // a callback no model que atualiza o status.
  //
  const handleSubmit = async () => {
    try {
      const result = await updateInventory(inventoryId, {
        updateAt: new Date(),
      });
      if (result.ok) {
        notifySuccess("Inventário atualizados com sucesso!");
      } else {
        notifyError(result.message || "Erro ao atualizar.");
      }
    } catch (error) {
      console.error("Erro ao se comunicar com o servidor.");
      notifyError("Erro ao atualizar veículos do inventário");
    }

    closeChangeStatusModal();
  };

  if (!isChangeStatusVehiclesOpen) return null;

  return (
    <Modal
      show={isChangeStatusVehiclesOpen}
      onClose={closeChangeStatusModal}
      size="5xl"
    >
      <Modal.Header>Gerenciar Inventário e Veículos</Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          {currentInventory && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Inventário: {currentInventory.city}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Status atual:</strong>{" "}
                  {INVENTORY_STATUS_LABELS[currentInventory?.situation]}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Total de veículos:</strong>{" "}
                  {currentInventory?.vehicles?.length || 0}
                </p>
              </div>
            </div>
          )}
          {currentInventory?.vehicles?.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-3">
                Veículos neste inventário:{" "}
              </h3>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                <ul className="space-y-2">
                  {currentInventory?.vehicles.map((vehicle, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between p-2 border-b border-gray-100"
                    >
                      <div className="flex-1">
                        <span className="font-medium text-sm">
                          {vehicle.licensePlate}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-md font-semibold text-gray-700 mb-3">
              Gerenciar Status dos Veículos:
            </h3>
            <div
              id="scrollableDiv"
              className="max-h-96 overflow-y-auto border border-gray-200 rounded-md"
            >
              <InfiniteScroll
                dataLength={allVehicles.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                }
                endMessage={
                  <div className="text-center py-4 text-sm text-gray-500">
                    Todos os veículos foram carregados
                  </div>
                }
                scrollableTarget="scrollableDiv"
              >
                <table className="min-w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Placa
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Status Atual
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Novo Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allVehicles.map((vehicle) => {
                      const currentStatus = vehicle.status;
                      const availableOptions =
                        getAvailableStatusOptions(currentStatus);
                      const selectedStatus = vehicleStatuses[vehicle._id] || "";

                      return (
                        <tr key={vehicle._id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium">
                            {vehicle.licensePlate}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {STATUS_LABELS[vehicle.status]}
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={selectedStatus}
                              onChange={(e) =>
                                handleVehicleStatusChange(
                                  vehicle._id,
                                  e.target.value,
                                )
                              }
                              className="w-full text-sm p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              disabled={availableOptions.length <= 1}
                            >
                              {availableOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </InfiniteScroll>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between w-full items-center">
          <span className="text-sm text-gray-500">
            {allVehicles.length} veículos carregados
          </span>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400 transition-colors"
              onClick={closeChangeStatusModal}
            >
              Cancelar
            </button>
            <button
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-green-300"
              onClick={handleSubmit}
            >
              Salvar alterações
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
